// cleaningScheduleApi.js — Community shift scheduling (Schedule a Cleaning)
//
// Community schedule routes: api.innoworx.ai/Stage (CloudFront; same path as execute-api Stage).
// Override: REACT_APP_COMMUNITY_SCHEDULE_API_BASE_URL or REACT_APP_API_BASE_URL
//
// Schedule a Cleaning uses schedule_layout "personnel" (bulk upsert).
// Slot index: request/response field `index` (0-based); UI "Shift - N" = index + 1.
// user_ids: Cognito sub (from GET /community/schedule/users → user_id).
// generate_tasks: default false; task rows only from housekeeping floors with selected:true.

import { fetchAuthSession } from 'aws-amplify/auth';

import {
  buildCleaningScheduleBulkPayload,
  CLEANING_SCHEDULE_BULK_ENDPOINT,
} from './cleaningSchedulePayload';
import {
  filterEmployeesByCategory,
  PERSONNEL_TYPE_BY_CATEGORY_TAB,
} from './cleaningAreaData';
import { employeeApi } from '../ShiftManagement/employeeApi';
import {
  communityScheduleItemToShift,
  extractScheduleUsersArray,
  mapLocationsToSiteCatalog,
  mapScheduleUsersToEmployees,
  mapTaskTemplatesToCatalog,
  normalizeScheduleListResponse,
} from './cleaningScheduleMappers';

export { CLEANING_SCHEDULE_BULK_ENDPOINT, buildCleaningScheduleBulkPayload };

const DEFAULT_COMMUNITY_API_BASE =
  process.env.REACT_APP_API_BASE_URL || 'https://api.innoworx.ai/Stage';

/**
 * Community schedule APIs (locations, bulk, users, etc.).
 * Dev: `/Stage` → setupProxy.js → api.innoworx.ai (matches backend Postman tests).
 */
export const COMMUNITY_SCHEDULE_API_BASE_URL =
  process.env.REACT_APP_COMMUNITY_SCHEDULE_API_BASE_URL ||
  (process.env.NODE_ENV === 'development' ? '/Stage' : DEFAULT_COMMUNITY_API_BASE);

const getAuthToken = async () => {
  try {
    const { tokens } = await fetchAuthSession();
    if (!tokens?.idToken) {
      console.error('[cleaningScheduleApi] No ID token in session');
      return null;
    }

    const currentTime = Math.floor(Date.now() / 1000);
    if (tokens.idToken.payload.exp <= currentTime) {
      console.error('[cleaningScheduleApi] ID token expired');
      return null;
    }

    return tokens.idToken.toString();
  } catch (error) {
    console.error('[cleaningScheduleApi] Auth session error:', error);
    return null;
  }
};

const parseErrorMessage = async response => {
  try {
    const body = await response.json();
    return body.error || body.message || body.body || response.statusText;
  } catch {
    return response.statusText;
  }
};

/** Resolves absolute or dev-proxy-relative community schedule URLs for fetch + query params. */
const buildCommunityUrl = path => {
  if (path.startsWith('http')) {
    return new URL(path);
  }

  const endpoint = path.startsWith('/') ? path : `/${path}`;
  const base = COMMUNITY_SCHEDULE_API_BASE_URL.replace(/\/$/, '');

  if (/^https?:\/\//i.test(base)) {
    return new URL(`${base}${endpoint}`);
  }

  const origin =
    typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000';
  return new URL(`${base}${endpoint}`, origin);
};

const communityFetch = async (path, { method = 'GET', body, query } = {}) => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Authentication token not available. Please sign in again.');
  }

  const url = buildCommunityUrl(path);

  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value != null && value !== '') {
        url.searchParams.set(key, value);
      }
    });
  }

  const response = await fetch(url.toString(), {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    ...(body != null ? { body: JSON.stringify(body) } : {}),
  });

  if (!response.ok) {
    const message = await parseErrorMessage(response);
    const error = new Error(message || `Request failed (${response.status})`);
    error.status = response.status;
    throw error;
  }

  if (response.status === 204) {
    return null;
  }

  const text = await response.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

/** @param {{ site_id?: string, id?: string, tenant_site_id?: string }} site */
export const resolveCommunitySiteId = site =>
  site?.site_id || site?.id || site?.tenant_site_id || null;

/**
 * @param {string} siteId
 * @returns {Promise<{ blocks: object[], amenities: object[], tasks: object[], templatesError?: string }>}
 */
export const fetchCleaningSiteCatalog = async siteId => {
  if (!siteId) {
    throw new Error('site_id is required to load the cleaning site catalog');
  }

  const [locationsResult, templatesResult] = await Promise.allSettled([
    communityFetch('/community/schedule/locations', { query: { site_id: siteId } }),
    communityFetch(`/community/sites/${siteId}/task-templates`),
  ]);

  if (locationsResult.status === 'rejected') {
    console.error(
      '[cleaningScheduleApi] GET /community/schedule/locations failed',
      { siteId, error: locationsResult.reason }
    );
    throw locationsResult.reason;
  }

  const rawLocations = locationsResult.value;
  const locationCatalog = mapLocationsToSiteCatalog(rawLocations);

  let tasks = [];
  let templatesError;
  if (templatesResult.status === 'fulfilled') {
    tasks = mapTaskTemplatesToCatalog(templatesResult.value);
  } else {
    const err = templatesResult.reason;
    templatesError = err?.message || String(err);
    console.warn('[cleaningScheduleApi] task-templates request failed:', err);
  }

  const catalog = {
    ...locationCatalog,
    tasks,
    ...(templatesError ? { templatesError } : {}),
  };

  console.group('[cleaningScheduleApi] Site catalog loaded');
  console.log('site_id:', siteId);
  console.log('API base:', COMMUNITY_SCHEDULE_API_BASE_URL);
  console.log('Raw locations response:', rawLocations);
  console.log('Mapped blocks:', catalog.blocks);
  console.log('Mapped amenities:', catalog.amenities);
  console.log('Mapped tasks:', catalog.tasks);
  console.log('Counts:', {
    blocks: catalog.blocks.length,
    amenities: catalog.amenities.length,
    tasks: catalog.tasks.length,
  });
  if (catalog.blocks.length === 0) {
    console.warn(
      'No blocks after mapping — backend may have empty facility-blocks for this site/tenant, or response shape differs from expected { blocks: [{ block_id, floors }] }'
    );
  }
  console.groupEnd();

  return catalog;
};

/**
 * @param {string} siteId
 * @param {string} [personnelType] supervisors | employees | housekeeping | security
 */
export const getCleaningScheduleUsers = async (siteId, personnelType) => {
  const query = { site_id: siteId };
  if (personnelType) {
    query.personnel_type = personnelType;
  }

  const data = await communityFetch('/community/schedule/users', { query });
  const users = mapScheduleUsersToEmployees(data, personnelType);

  console.log('[cleaningScheduleApi] GET /community/schedule/users', {
    siteId,
    personnelType: personnelType || '(all)',
    rawCount: extractScheduleUsersArray(data).length,
    mappedCount: users.length,
  });

  return users;
};

const mapLegacyEmployees = (list, source) =>
  (Array.isArray(list) ? list : []).map(user => ({
    user_id: user.user_id || user.employee_id || user.cognito_sub,
    employee_id: user.employee_id || user.user_id,
    user_name: user.user_name || user.name || user.employee_name || 'Unknown',
    name: user.user_name || user.name || user.employee_name || 'Unknown',
    display_name: user.user_name || user.name || user.employee_name || 'Unknown',
    role_name: user.spec_role || user.role_name || '',
    spec_role: user.spec_role || user.role_name || '',
    image_path: user.image_path || '',
    _source: source,
  }));

/**
 * All users for a site (unfiltered). Falls back to legacy /user/guards + /user/supervisors if empty.
 */
export const fetchAllScheduleUsersForSite = async siteId => {
  let users = await getCleaningScheduleUsers(siteId);
  if (users.length > 0) return users;

  const [guards, supervisors] = await Promise.all([
    employeeApi.fetchAvailableGuards().catch(() => []),
    employeeApi.fetchAvailableSupervisors().catch(() => []),
  ]);

  const legacy = [
    ...mapLegacyEmployees(supervisors, 'supervisor'),
    ...mapLegacyEmployees(guards, 'guard'),
  ];

  const byId = new Map();
  legacy.forEach(emp => {
    const id = emp.user_id || emp.employee_id;
    if (id && !byId.has(id)) byId.set(id, emp);
  });

  console.warn(
    '[cleaningScheduleApi] community schedule users empty; using legacy guards/supervisors',
    { siteId, legacyCount: byId.size }
  );

  return Array.from(byId.values());
};

/**
 * Users for one Select employee tab — personnel_type API, then all-users filter, then legacy.
 */
export const getCleaningScheduleUsersForCategory = async (siteId, categoryId) => {
  const personnelType = PERSONNEL_TYPE_BY_CATEGORY_TAB[categoryId];

  let users = personnelType
    ? await getCleaningScheduleUsers(siteId, personnelType)
    : [];

  if (users.length === 0) {
    const all = await fetchAllScheduleUsersForSite(siteId);
    users = filterEmployeesByCategory(all, categoryId);
  }

  return users;
};

/**
 * Calendar highlight dates.
 * @returns {Promise<string[]>} YYYY-MM-DD
 */
export const getCleaningScheduleDates = async (siteId, from, to) => {
  const data = await communityFetch('/community/schedule/dates', {
    query: { site_id: siteId, from, to },
  });
  return data?.dates || [];
};

/**
 * Load community cleaning shifts for a site and date.
 * @param {string} siteId
 * @param {string} date YYYY-MM-DD
 * @param {object} [catalog] Optional site catalog for mapping personnel → UI
 * @returns {Promise<object[]>} Shift rows for ScheduleACleaning
 */
export const getCleaningRoster = async (siteId, date, catalog = null) => {
  const data = await communityFetch('/community/schedule', {
    query: { site_id: siteId, date },
  });

  const items = normalizeScheduleListResponse(data);
  return items.map(item => communityScheduleItemToShift(item, catalog || {}));
};

/**
 * POST /community/schedule/bulk
 * @param {string} siteId
 * @param {Object} data Bulk body or legacy schedule object
 * @param {Object} [options]
 * @param {string} [options.tenantId]
 * @param {Object} [options.datePickerData]
 * @param {Object[]} [options.shifts]
 * @param {Object} [options.siteCatalog] blocks + tasks for payload mapping
 * @returns {Promise<{ message: string, ss_id?: string, results?: object[], processed_dates?: number }>}
 */
export const sendCleaningSchedule = async (siteId, data, options = {}) => {
  const bulkPayload =
    data?.selected_dates != null && Array.isArray(data?.shifts)
      ? { ...data, site_id: data.site_id || siteId }
      : buildCleaningScheduleBulkPayload({
          siteId,
          datePickerData: options.datePickerData ?? {
            mode: data?.mode,
            singleDate: data?.date ? new Date(data.date) : null,
            startDate: data?.date_range?.[0] ? new Date(data.date_range[0]) : null,
            endDate: data?.date_range?.[1] ? new Date(data.date_range[1]) : null,
          },
          shifts: options.shifts ?? data?.shifts ?? [],
          siteCatalog: options.siteCatalog ?? data?.siteCatalog,
        });

  const cleanedShifts = (bulkPayload.shifts || []).map(shift => {
    const { shift_start_time, shift_end_time, ...rest } = shift;
    return rest;
  });

  const response = await communityFetch(CLEANING_SCHEDULE_BULK_ENDPOINT, {
    method: 'POST',
    body: {
      site_id: bulkPayload.site_id,
      selected_dates: bulkPayload.selected_dates,
      generate_tasks: bulkPayload.generate_tasks ?? false,
      shifts: cleanedShifts,
    },
  });

  const results = response?.results || [];
  const processed_dates = new Set(
    results.map(r => r.date).filter(Boolean)
  ).size;

  const firstResult =
    results.find(r => r.status === 'created' || r.status === 'updated') ||
    results[0];

  return {
    message: 'Community shift saved successfully',
    ss_id: firstResult?.ss_id,
    results,
    processed_dates: processed_dates || bulkPayload.selected_dates?.length || 0,
    raw: response,
  };
};

/**
 * DELETE /community/schedule
 * @param {string} scheduleId ss_id
 * @param {Object} [options]
 * @param {string} options.siteId
 * @param {string} [options.date] YYYY-MM-DD
 * @param {number} [options.index] 0-based shift index
 */
export const deleteCleaningSchedule = async (scheduleId, options = {}) => {
  const { siteId, date, index } = options;

  if (!siteId) {
    throw new Error('site_id is required to delete a cleaning schedule');
  }

  const body = scheduleId
    ? { site_id: siteId, ss_id: scheduleId }
    : { site_id: siteId, date, index };

  if (!body.ss_id && (body.date == null || body.index == null)) {
    throw new Error('ss_id or date + index is required to delete a cleaning schedule');
  }

  await communityFetch('/community/schedule', {
    method: 'DELETE',
    body,
  });

  return { message: 'Community shift deleted successfully' };
};

/**
 * Dry-run overlap check before save.
 */
export const checkCleaningScheduleConflicts = async payload => {
  return communityFetch('/community/schedule/conflicts', {
    method: 'POST',
    body: payload,
  });
};

export const repeatCleaningSchedule = async payload => {
  return communityFetch('/community/schedule/repeat', {
    method: 'POST',
    body: payload,
  });
};
